import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, fmtDate, inr, STATUS_CLASS, STATUS_LABEL } from "@/lib/api";
import { AdminCard } from "./AdminLayout";

const Stat = ({ label, value, sub, testId }) => (
  <div className="rounded-lg border border-border bg-white p-4" data-testid={testId}>
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-2xl font-semibold">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export default function AdminDashboard() {
  const { data: s, isLoading } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, staleTime: 10_000 });
  if (isLoading || !s) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-medium tracking-tight">Sales overview</h1>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Revenue (paid)" value={inr(s.revenue)} sub={`${s.paid_orders} paid orders`} testId="stat-revenue" />
        <Stat label="To ship" value={s.to_ship} sub="paid + packed, awaiting dispatch" testId="stat-to-ship" />
        <Stat label="Paperbacks sold" value={s.physical_units} testId="stat-physical" />
        <Stat label="Ebooks sold" value={s.ebook_units} testId="stat-ebook" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <AdminCard title="Recent orders" className="lg:col-span-2">
          <table className="w-full text-sm">
            <tbody>
              {s.recent_orders.map((o) => (
                <tr key={o.order_id} className="border-b border-border/60 last:border-0">
                  <td className="py-2"><Link to={`/admin/orders/${o.order_id}`} className="font-mono text-xs font-semibold hover:text-primary" data-testid={`recent-order-${o.order_id}`}>{o.order_id}</Link></td>
                  <td className="py-2 text-muted-foreground">{o.customer_name}</td>
                  <td className="py-2 text-xs text-muted-foreground">{fmtDate(o.created_at)}</td>
                  <td className="py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLASS[o.status]}`}>{STATUS_LABEL[o.status]}</span></td>
                  <td className="py-2 text-right font-semibold">{inr(o.total)}</td>
                </tr>
              ))}
              {s.recent_orders.length === 0 && <tr><td className="py-6 text-center text-muted-foreground">No orders yet</td></tr>}
            </tbody>
          </table>
        </AdminCard>
        <div className="space-y-4">
          <AdminCard title="By title">
            <ul className="space-y-2 text-sm">
              {s.by_book.map((b) => <li key={b.title} className="flex justify-between"><span className="truncate">{b.title}</span><span className="text-muted-foreground">{b.units} · {inr(b.revenue)}</span></li>)}
              {s.by_book.length === 0 && <li className="text-muted-foreground">No sales yet</li>}
            </ul>
          </AdminCard>
          <AdminCard title="Low stock">
            <ul className="space-y-2 text-sm" data-testid="low-stock-list">
              {s.low_stock.map((b) => <li key={b.book_id} className="flex justify-between"><span className="truncate">{b.title}</span><span className={b.stock === 0 ? "font-semibold text-destructive" : "text-primary"}>{b.stock} left</span></li>)}
              {s.low_stock.length === 0 && <li className="text-muted-foreground">All titles well stocked</li>}
            </ul>
          </AdminCard>
          <AdminCard title="Orders by status">
            <ul className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(s.by_status).map(([k, v]) => <li key={k} className="flex justify-between rounded bg-muted px-2 py-1"><span>{STATUS_LABEL[k]}</span><span className="font-semibold">{v}</span></li>)}
            </ul>
          </AdminCard>
        </div>
      </div>
    </div>
  );
}
